import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Loader2, Printer, ReceiptText, RefreshCw } from 'lucide-react'
import toast from 'react-hot-toast'
import { feesApi } from '../api/services.js'
import PageHeader from '../components/PageHeader.jsx'

const formatCurrency = (value = 0) => `₹${Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const formatDate = (value) =>
  value
    ? new Intl.DateTimeFormat('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }).format(new Date(value))
    : '—'

const paymentModeLabel = (value = '') =>
  value
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ') || '—'

const escapeHtml = (value = '') =>
  String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')

const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen']
const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety']

function numberToWords(value) {
  if (value === 0) return 'Zero'
  if (value < 20) return ones[value]
  if (value < 100) return `${tens[Math.floor(value / 10)]}${value % 10 ? ` ${ones[value % 10]}` : ''}`
  if (value < 1000) return `${ones[Math.floor(value / 100)]} Hundred${value % 100 ? ` ${numberToWords(value % 100)}` : ''}`
  if (value < 100000) return `${numberToWords(Math.floor(value / 1000))} Thousand${value % 1000 ? ` ${numberToWords(value % 1000)}` : ''}`
  if (value < 10000000) return `${numberToWords(Math.floor(value / 100000))} Lakh${value % 100000 ? ` ${numberToWords(value % 100000)}` : ''}`
  return `${numberToWords(Math.floor(value / 10000000))} Crore${value % 10000000 ? ` ${numberToWords(value % 10000000)}` : ''}`
}

function amountInWords(amount) {
  const rupees = Math.floor(Number(amount || 0))
  const paise = Math.round((Number(amount || 0) - rupees) * 100)
  let label = `Rupees ${numberToWords(rupees)}`
  if (paise > 0) {
    label += ` and ${numberToWords(paise)} Paise`
  }
  return `${label} Only`
}

function buildPrintMarkup(receipt, lineItems) {
  const adjustmentRows = [
    receipt?.totals?.discountAmount > 0
      ? `<div class="adj-row"><span>Total Discount</span><strong>- ${formatCurrency(receipt.totals.discountAmount)}</strong></div>`
      : '',
    receipt?.totals?.concessionAmount > 0
      ? `<div class="adj-row"><span>Total Concession</span><strong>- ${formatCurrency(receipt.totals.concessionAmount)}</strong></div>`
      : '',
    receipt?.totals?.fineAmount > 0
      ? `<div class="adj-row"><span>Total Fine Added</span><strong>+ ${formatCurrency(receipt.totals.fineAmount)}</strong></div>`
      : '',
  ]
    .filter(Boolean)
    .join('')

  const lineRows = lineItems
    .map((item) => {
      const notes = [
        item.discount > 0 ? `Discount - ${formatCurrency(item.discount)}` : '',
        item.concession > 0 ? `Concession - ${formatCurrency(item.concession)}` : '',
        item.fine > 0 ? `Fine + ${formatCurrency(item.fine)}` : '',
      ]
        .filter(Boolean)
        .join(' • ')

      return `
        <tr>
          <td>
            <div class="item-label">${escapeHtml(item.label)}</div>
            ${notes ? `<div class="item-note">${escapeHtml(notes)}</div>` : ''}
          </td>
          <td>${formatCurrency(item.netAmount)}</td>
        </tr>
      `
    })
    .join('')

  const schoolLogo = receipt?.school?.logo ? `<img src="${escapeHtml(receipt.school.logo)}" alt="School logo" class="logo" />` : ''
  const parentLine = receipt?.student?.parentName?.name
    ? `<div class="meta-row"><span>${escapeHtml(receipt.student.parentName.label)}:</span><strong>${escapeHtml(receipt.student.parentName.name)}</strong></div>`
    : ''

  return `
    <!doctype html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>${escapeHtml(receipt?.receiptNumber || 'Fee Receipt')}</title>
        <style>
          * { box-sizing: border-box; }
          body { font-family: Arial, sans-serif; margin: 0; color: #0f172a; background: #f8fafc; }
          .page { max-width: 760px; margin: 0 auto; background: #fff; min-height: 100vh; padding: 32px; }
          .top { display: flex; justify-content: space-between; gap: 24px; align-items: flex-start; border-bottom: 2px solid #0f172a; padding-bottom: 20px; }
          .branding { display: flex; gap: 16px; align-items: flex-start; }
          .logo { width: 72px; height: 72px; object-fit: contain; border-radius: 16px; }
          .school { font-size: 28px; font-weight: 800; line-height: 1.1; }
          .sub { font-size: 13px; color: #475569; margin-top: 4px; }
          .badge { border: 1px solid #cbd5e1; border-radius: 999px; padding: 8px 14px; text-transform: uppercase; letter-spacing: 0.12em; font-size: 11px; font-weight: 700; color: #0f172a; }
          .grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 18px; margin-top: 24px; }
          .panel { border: 1px solid #e2e8f0; border-radius: 18px; padding: 16px; background: #fff; }
          .panel h3 { margin: 0 0 12px; font-size: 14px; text-transform: uppercase; letter-spacing: 0.08em; color: #64748b; }
          .meta-row { display: flex; justify-content: space-between; gap: 16px; margin-bottom: 8px; font-size: 14px; }
          .meta-row span { color: #475569; }
          table { width: 100%; border-collapse: collapse; margin-top: 24px; }
          th { text-align: left; font-size: 12px; text-transform: uppercase; letter-spacing: 0.08em; color: #64748b; padding-bottom: 10px; border-bottom: 1px solid #e2e8f0; }
          td { padding: 14px 0; border-bottom: 1px solid #f1f5f9; font-size: 14px; vertical-align: top; }
          td:last-child, th:last-child { text-align: right; }
          .item-label { font-weight: 600; color: #0f172a; }
          .item-note { margin-top: 4px; font-size: 12px; color: #64748b; }
          .totals { margin-top: 24px; display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; }
          .total-card { border-radius: 18px; padding: 16px; background: #f8fafc; border: 1px solid #e2e8f0; }
          .total-card .label { font-size: 12px; color: #64748b; text-transform: uppercase; letter-spacing: 0.08em; }
          .total-card .value { margin-top: 8px; font-size: 20px; font-weight: 800; color: #0f172a; }
          .words { margin-top: 18px; border: 1px dashed #cbd5e1; border-radius: 18px; padding: 14px 16px; font-size: 13px; color: #475569; background: #f8fafc; }
          .adjustments { margin-top: 18px; border: 1px solid #dbeafe; border-radius: 18px; padding: 16px; background: #eff6ff; }
          .adjustments h4 { margin: 0 0 10px; font-size: 13px; color: #1d4ed8; text-transform: uppercase; letter-spacing: 0.08em; }
          .adj-row { display: flex; justify-content: space-between; gap: 16px; margin-bottom: 8px; font-size: 14px; color: #1e3a8a; }
          .footer { margin-top: 36px; padding-top: 18px; border-top: 1px solid #e2e8f0; display: flex; justify-content: space-between; gap: 24px; font-size: 12px; color: #64748b; }
        </style>
      </head>
      <body>
        <div class="page">
          <div class="top">
            <div class="branding">
              ${schoolLogo}
              <div>
                <div class="school">${escapeHtml(receipt?.school?.name || 'School')}</div>
                ${receipt?.school?.address ? `<div class="sub">${escapeHtml(receipt.school.address)}</div>` : ''}
                ${receipt?.school?.phone ? `<div class="sub">Phone: ${escapeHtml(receipt.school.phone)}</div>` : ''}
              </div>
            </div>
            <div class="badge">Fee Receipt</div>
          </div>

          <div class="grid">
            <div class="panel">
              <h3>Receipt Details</h3>
              <div class="meta-row"><span>Receipt No.</span><strong>${escapeHtml(receipt?.receiptNumber || '—')}</strong></div>
              <div class="meta-row"><span>Payment Date</span><strong>${escapeHtml(formatDate(receipt?.receiptDate))}</strong></div>
              <div class="meta-row"><span>Payment Mode</span><strong>${escapeHtml(paymentModeLabel(receipt?.paymentMode))}</strong></div>
              <div class="meta-row"><span>Academic Year</span><strong>${escapeHtml(receipt?.academicYear || '—')}</strong></div>
              <div class="meta-row"><span>Reference</span><strong>${escapeHtml(receipt?.referenceNumber || '—')}</strong></div>
            </div>
            <div class="panel">
              <h3>Student Details</h3>
              <div class="meta-row"><span>Student</span><strong>${escapeHtml(receipt?.student?.name || '—')}</strong></div>
              ${parentLine}
              <div class="meta-row"><span>Class</span><strong>${escapeHtml(receipt?.student?.class || '—')}</strong></div>
              <div class="meta-row"><span>Admission No.</span><strong>${escapeHtml(receipt?.student?.admissionNo || '—')}</strong></div>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>Particulars</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>${lineRows}</tbody>
          </table>

          <div class="totals">
            <div class="total-card">
              <div class="label">This Payment</div>
              <div class="value">${formatCurrency(receipt?.totals?.thisPayment || 0)}</div>
            </div>
            <div class="total-card">
              <div class="label">Previously Paid</div>
              <div class="value">${formatCurrency(receipt?.totals?.previouslyPaid || 0)}</div>
            </div>
            <div class="total-card">
              <div class="label">Outstanding</div>
              <div class="value">${formatCurrency(receipt?.totals?.outstanding || 0)}</div>
            </div>
          </div>

          <div class="words">${escapeHtml(amountInWords(receipt?.totals?.thisPayment || 0))}</div>

          ${adjustmentRows ? `<div class="adjustments"><h4>Account Adjustments</h4>${adjustmentRows}</div>` : ''}

          <div class="footer">
            <span>${escapeHtml(receipt?.notes || 'Official fee receipt generated by BrainCamp admin')}</span>
            <span>Generated on ${escapeHtml(formatDate(new Date()))}</span>
          </div>
        </div>
      </body>
    </html>
  `
}

export default function FeeReceiptPage() {
  const { paymentId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [receipt, setReceipt] = useState(null)
  const [loading, setLoading] = useState(true)

  const fetchReceipt = async () => {
    try {
      setLoading(true)
      const data = await feesApi.getReceipt(paymentId)
      setReceipt(data || null)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load receipt')
      setReceipt(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchReceipt()
  }, [paymentId])

  const lineItems = useMemo(() => {
    return (receipt?.particulars || []).flatMap((group) => {
      if (group.installments?.length) {
        return group.installments.map((item) => ({
          label: item.label || group.name || 'Fee',
          amount: Number(item.amount || 0),
          discount: Number(item.discount || 0),
          concession: Number(item.concession || 0),
          fine: Number(item.fine || 0),
          netAmount: Number(item.amount || 0) - Number(item.discount || 0) - Number(item.concession || 0) + Number(item.fine || 0),
        }))
      }

      return [
        {
          label: group.name || 'Fee',
          amount: Number(group.subtotal || receipt?.totals?.thisPayment || 0),
          discount: 0,
          concession: 0,
          fine: 0,
          netAmount: Number(group.subtotal || receipt?.totals?.thisPayment || 0),
        },
      ]
    })
  }, [receipt])

  const fromAccountPath = location.state?.accountPath

  const handlePrint = () => {
    if (!receipt) return

    const printWindow = window.open('', '_blank', 'noopener,noreferrer,width=980,height=900')
    if (!printWindow) {
      toast.error('Allow pop-ups to print the receipt')
      return
    }

    printWindow.document.write(buildPrintMarkup(receipt, lineItems))
    printWindow.document.close()
    printWindow.focus()
    setTimeout(() => {
      printWindow.print()
    }, 150)
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Fee Receipt"
        subtitle="Review the official payment receipt exactly as your admin team recorded it."
        chips={[
          receipt?.receiptNumber || 'Receipt loading',
          receipt?.academicYear || 'Academic year unavailable',
          receipt ? paymentModeLabel(receipt.paymentMode) : 'Payment mode',
        ]}
        actions={
          <>
            <button onClick={() => navigate(fromAccountPath || '/fees/accounts')} className="btn-secondary">
              <ArrowLeft className="w-4 h-4" /> {fromAccountPath ? 'Back to Account' : 'Back to Fees'}
            </button>
            <button onClick={fetchReceipt} className="btn-secondary">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <button onClick={handlePrint} disabled={!receipt} className="btn-primary">
              <Printer className="w-4 h-4" /> Print Receipt
            </button>
          </>
        }
      />

      {loading ? (
        <div className="card p-12 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-gray-300" />
        </div>
      ) : !receipt ? (
        <div className="empty-state card">
          <ReceiptText className="w-10 h-10 text-gray-200" />
          <p className="empty-state-title">Receipt not available</p>
          <p className="empty-state-copy">This payment receipt could not be loaded. Try refreshing, or reopen it from the fee account history.</p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="border-b border-gray-100 bg-slate-900 px-6 py-6 text-white">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
              <div className="flex items-start gap-4 min-w-0">
                {receipt.school?.logo ? (
                  <img
                    src={receipt.school.logo}
                    alt={receipt.school.name || 'School logo'}
                    className="h-16 w-16 rounded-2xl border border-white/10 bg-white/5 object-cover"
                  />
                ) : (
                  <div className="h-16 w-16 rounded-2xl border border-white/10 bg-white/5 flex items-center justify-center">
                    <ReceiptText className="w-7 h-7 text-white/80" />
                  </div>
                )}
                <div className="min-w-0">
                  <p className="text-2xl font-bold tracking-tight truncate">{receipt.school?.name || 'School'}</p>
                  {receipt.school?.address && <p className="text-sm text-white/70 mt-1">{receipt.school.address}</p>}
                  {receipt.school?.phone && <p className="text-sm text-white/70 mt-1">Phone: {receipt.school.phone}</p>}
                </div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-4 min-w-[220px]">
                <p className="text-xs uppercase tracking-[0.18em] text-white/60 font-semibold">Fee Receipt</p>
                <p className="text-xl font-bold mt-3">{receipt.receiptNumber || '—'}</p>
                <p className="text-sm text-white/70 mt-1">{formatDate(receipt.receiptDate)}</p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-2xl border border-gray-100 bg-gray-50 p-5">
                <p className="text-xs uppercase tracking-[0.12em] text-gray-400 font-semibold">Receipt Details</p>
                <div className="mt-4 space-y-3 text-sm">
                  <div className="flex justify-between gap-4">
                    <span className="text-gray-500">Payment mode</span>
                    <span className="font-semibold text-gray-900">{paymentModeLabel(receipt.paymentMode)}</span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span className="text-gray-500">Academic year</span>
                    <span className="font-semibold text-gray-900">{receipt.academicYear || '—'}</span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span className="text-gray-500">Reference number</span>
                    <span className="font-semibold text-gray-900">{receipt.referenceNumber || '—'}</span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span className="text-gray-500">Receipt date</span>
                    <span className="font-semibold text-gray-900">{formatDate(receipt.receiptDate)}</span>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-gray-100 bg-gray-50 p-5">
                <p className="text-xs uppercase tracking-[0.12em] text-gray-400 font-semibold">Student Details</p>
                <div className="mt-4 space-y-3 text-sm">
                  <div className="flex justify-between gap-4">
                    <span className="text-gray-500">Student</span>
                    <span className="font-semibold text-gray-900 text-right">{receipt.student?.name || '—'}</span>
                  </div>
                  {receipt.student?.parentName?.name && (
                    <div className="flex justify-between gap-4">
                      <span className="text-gray-500">{receipt.student.parentName.label}</span>
                      <span className="font-semibold text-gray-900 text-right">{receipt.student.parentName.name}</span>
                    </div>
                  )}
                  <div className="flex justify-between gap-4">
                    <span className="text-gray-500">Class</span>
                    <span className="font-semibold text-gray-900 text-right">{receipt.student?.class || '—'}</span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span className="text-gray-500">Admission number</span>
                    <span className="font-semibold text-gray-900 text-right">{receipt.student?.admissionNo || '—'}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-gray-50 px-5 py-3 flex items-center justify-between text-xs font-semibold uppercase tracking-[0.1em] text-gray-500">
                <span>Particulars</span>
                <span>Amount</span>
              </div>
              <div className="divide-y divide-gray-100">
                {lineItems.map((item, index) => {
                  const adjustments = [
                    item.discount > 0 ? `Discount - ${formatCurrency(item.discount)}` : '',
                    item.concession > 0 ? `Concession - ${formatCurrency(item.concession)}` : '',
                    item.fine > 0 ? `Fine + ${formatCurrency(item.fine)}` : '',
                  ].filter(Boolean)

                  return (
                    <div key={`${item.label}-${index}`} className="px-5 py-4 flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900">{item.label}</p>
                        {adjustments.length > 0 && <p className="text-xs text-gray-500 mt-1">{adjustments.join(' • ')}</p>}
                      </div>
                      <p className="text-right font-semibold text-gray-900 flex-shrink-0">{formatCurrency(item.netAmount)}</p>
                    </div>
                  )
                })}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              {[
                { label: 'This Payment', value: receipt.totals?.thisPayment || 0, tone: 'text-primary-700 bg-primary-50' },
                { label: 'Previously Paid', value: receipt.totals?.previouslyPaid || 0, tone: 'text-green-700 bg-green-50' },
                { label: 'Outstanding', value: receipt.totals?.outstanding || 0, tone: 'text-red-600 bg-red-50' },
              ].map((item) => (
                <div key={item.label} className="rounded-2xl border border-gray-100 bg-white p-5">
                  <p className="text-xs uppercase tracking-[0.12em] text-gray-400 font-semibold">{item.label}</p>
                  <p className={`text-2xl font-bold mt-3 ${item.tone.split(' ')[0]}`}>{formatCurrency(item.value)}</p>
                </div>
              ))}
            </div>

            {(receipt.totals?.discountAmount || receipt.totals?.concessionAmount || receipt.totals?.fineAmount) ? (
              <div className="rounded-2xl border border-blue-100 bg-blue-50 p-5">
                <p className="text-xs uppercase tracking-[0.12em] text-blue-500 font-semibold">Account Adjustments</p>
                <div className="mt-4 grid gap-3 md:grid-cols-3">
                  <div className="rounded-xl bg-white px-4 py-3 border border-blue-100">
                    <p className="text-sm text-gray-500">Discount</p>
                    <p className="text-lg font-bold text-green-700 mt-1">{formatCurrency(receipt.totals?.discountAmount || 0)}</p>
                  </div>
                  <div className="rounded-xl bg-white px-4 py-3 border border-blue-100">
                    <p className="text-sm text-gray-500">Concession</p>
                    <p className="text-lg font-bold text-blue-700 mt-1">{formatCurrency(receipt.totals?.concessionAmount || 0)}</p>
                  </div>
                  <div className="rounded-xl bg-white px-4 py-3 border border-blue-100">
                    <p className="text-sm text-gray-500">Fine Added</p>
                    <p className="text-lg font-bold text-red-600 mt-1">{formatCurrency(receipt.totals?.fineAmount || 0)}</p>
                  </div>
                </div>
              </div>
            ) : null}

            <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 px-5 py-4 text-sm text-gray-600">
              <span className="font-semibold text-gray-900">Amount in words:</span> {amountInWords(receipt.totals?.thisPayment || 0)}
            </div>

            {receipt.notes ? (
              <div className="rounded-2xl border border-gray-100 bg-white px-5 py-4">
                <p className="text-xs uppercase tracking-[0.12em] text-gray-400 font-semibold">Notes</p>
                <p className="text-sm text-gray-600 mt-3">{receipt.notes}</p>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  )
}
